import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  School as SchoolIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  AccessTime as TimeIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

// SafeLine Pickup Color Scheme
const safeLineColors = {
  flameOrange: '#ff6b35',
  brightOrange: '#ff9500', 
  deepOrange: '#e55100',
  orangeTint: '#fff4f0',
  pureWhite: '#ffffff',
  lightGray: '#f8f9fa',
  coolGray: '#e9ecef',
  mediumGray: '#dee2e6',
  emerald: '#10b981',
  lightEmerald: '#d1fae5',
  forestGreen: '#047857',
  amber: '#f59e0b',
  lightAmber: '#fef3c7',
  darkAmber: '#d97706',
  red: '#ef4444',
  lightRed: '#fee2e2',
  darkRed: '#dc2626',
  darkCharcoal: '#1f2937',
  mediumCharcoal: '#374151',
  lightCharcoal: '#6b7280',
  mutedGray: '#9ca3af'
};

/**
 * SchoolSettings component with SafeLine theme matching verification pages
 */
const SchoolSettings = () => {
  const [settings, setSettings] = useState({
    // Basic Information
    schoolName: 'SafeLine Academy',
    schoolCode: 'SL001',
    address: '123 Education Street, City, State 12345',
    phone: '+1 (555) 123-4567',
    email: 'admin@safelineacademy.edu',
    website: 'https://www.safelineacademy.edu',
    
    // Operating Hours
    startTime: '08:00',
    endTime: '15:30',
    pickupStartTime: '14:30',
    pickupEndTime: '16:00',
    
    // Academic Settings
    academicYear: '2024-2025',
    currentSemester: 'Fall',
    gradeLevels: ['K', '1', '2', '3', '4', '5', '6', '7', '8'],
    
    // Security Settings
    requirePhotoID: true,
    requireLicensePlate: true,
    enableGuestLogging: true,
    maxGuestsPerDay: 10,
    autoApproveGuests: false,
    
    // System Settings
    enableMaintenanceMode: false,
    enableBackup: true,
    backupFrequency: 'daily',
    retentionDays: 30
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    
    setTimeout(() => {
      setLoading(false);
      setSnackbar({
        open: true,
        message: 'School settings saved successfully!',
        severity: 'success'
      });
    }, 1000);
  };

  const handleReset = () => {
    setSettings({
      schoolName: 'SafeLine Academy',
      schoolCode: 'SL001',
      address: '123 Education Street, City, State 12345',
      phone: '+1 (555) 123-4567',
      email: 'admin@safelineacademy.edu',
      website: 'https://www.safelineacademy.edu',
      startTime: '08:00',
      endTime: '15:30',
      pickupStartTime: '14:30',
      pickupEndTime: '16:00',
      academicYear: '2024-2025',
      currentSemester: 'Fall',
      gradeLevels: ['K', '1', '2', '3', '4', '5', '6', '7', '8'],
      requirePhotoID: true,
      requireLicensePlate: true,
      enableGuestLogging: true,
      maxGuestsPerDay: 10,
      autoApproveGuests: false,
      enableMaintenanceMode: false,
      enableBackup: true,
      backupFrequency: 'daily',
      retentionDays: 30
    });
    
    setSnackbar({
      open: true,
      message: 'Settings reset to defaults',
      severity: 'info'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const settingsSteps = [
    'Basic Information',
    'Operating Hours',
    'Security Settings',
    'System Configuration'
  ];

  return (
    <Box sx={{}}>
      <Grid container spacing={2}>
        {/* Settings Process Guide */}
        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SchoolIcon sx={{ color: safeLineColors.flameOrange, mr: 1.5 }} />
                  <Typography variant="h6" sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'bold' }}>
                    School Configuration
                  </Typography>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            <CardContent>
              <Stepper orientation="vertical">
                {settingsSteps.map((step, index) => (
                  <Step key={step} active={true}>
                    <StepLabel>
                      <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                        {step}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                        {index === 0 && 'Configure school name, contact information, and basic details'}
                        {index === 1 && 'Set operating hours and pickup time windows'}
                        {index === 2 && 'Manage security requirements and guest policies'}
                        {index === 3 && 'Configure system maintenance and backup settings'}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Form */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2,
                      backgroundColor: safeLineColors.flameOrange,
                      mr: 2
                    }}>
                      <SchoolIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        color: safeLineColors.darkCharcoal 
                      }}>
                        School Settings
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal 
                      }}>
                        Configure your school's information and operational settings
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            
            <CardContent sx={{ p: 3 }}>
              {/* Basic Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <BusinessIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Basic Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="School Name"
                      value={settings.schoolName}
                      onChange={(e) => handleSettingChange('schoolName', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="School Code"
                      value={settings.schoolCode}
                      onChange={(e) => handleSettingChange('schoolCode', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={settings.address}
                      onChange={(e) => handleSettingChange('address', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={settings.phone}
                      onChange={(e) => handleSettingChange('phone', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={settings.email}
                      onChange={(e) => handleSettingChange('email', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={settings.website}
                      onChange={(e) => handleSettingChange('website', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Operating Hours */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <TimeIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Operating Hours
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      School Hours
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Start Time"
                          type="time"
                          value={settings.startTime}
                          onChange={(e) => handleSettingChange('startTime', e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="End Time"
                          type="time"
                          value={settings.endTime}
                          onChange={(e) => handleSettingChange('endTime', e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      Pickup Hours
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Pickup Start"
                          type="time"
                          value={settings.pickupStartTime}
                          onChange={(e) => handleSettingChange('pickupStartTime', e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Pickup End"
                          type="time"
                          value={settings.pickupEndTime}
                          onChange={(e) => handleSettingChange('pickupEndTime', e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Security Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <SecurityIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Security Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      Guest Requirements
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.requirePhotoID}
                            onChange={(e) => handleSettingChange('requirePhotoID', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Require Photo ID
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.requireLicensePlate}
                            onChange={(e) => handleSettingChange('requireLicensePlate', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Require License Plate
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.enableGuestLogging}
                            onChange={(e) => handleSettingChange('enableGuestLogging', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Enable Guest Logging
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      Guest Limits
                    </Typography>
                    <TextField
                      fullWidth
                      label="Max Guests Per Day"
                      type="number"
                      value={settings.maxGuestsPerDay}
                      onChange={(e) => handleSettingChange('maxGuestsPerDay', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.autoApproveGuests}
                          onChange={(e) => handleSettingChange('autoApproveGuests', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                          Auto-approve Guests
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* System Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <SettingsIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  System Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableMaintenanceMode}
                          onChange={(e) => handleSettingChange('enableMaintenanceMode', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                          Enable Maintenance Mode
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableBackup}
                          onChange={(e) => handleSettingChange('enableBackup', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                          Enable Auto Backup
                        </Typography>
                      }
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Backup Frequency</InputLabel>
                      <Select
                        value={settings.backupFrequency}
                        onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                        label="Backup Frequency"
                        sx={{
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }}
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label="Retention Days"
                      type="number"
                      value={settings.retentionDays}
                      onChange={(e) => handleSettingChange('retentionDays', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{ mt: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  sx={{
                    borderColor: safeLineColors.flameOrange,
                    color: safeLineColors.flameOrange,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      backgroundColor: safeLineColors.orangeTint,
                      borderColor: safeLineColors.brightOrange
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{
                    background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolSettings; 